/**
 * Pricing API — Connect RPC implementation
 * Calls ProjectDataService via sabaody gateway.
 */ import { projectDataClient } from '../connect/clients';
import { proposalProjectApiConnect } from './proposalProjectApi.connect';
function toPlainObject(raw) {
    if (!raw) return {};
    if (typeof raw === 'object' && typeof raw.toJson === 'function') {
        try {
            const json = raw.toJson();
            if (json && typeof json === 'object') {
                return json;
            }
        } catch  {
        // Fall back to direct object access.
        }
    }
    return raw ?? {};
}
function readField(obj, ...keys) {
    for (const key of keys){
        if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
}
function toIsoString(value) {
    if (!value) return '';
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '' : value.toISOString();
    }
    if (typeof value === 'string') {
        const d = new Date(value);
        // Pricing CAS compares RFC3339Nano strings; Date would discard microseconds.
        return Number.isNaN(d.getTime()) ? '' : value.replace(/(\.\d*?[1-9])0+Z$/, '$1Z').replace(/\.0+Z$/, 'Z');
    }
    if (typeof value === 'object') {
        const ts = value;
        if (ts.seconds != null) {
            const seconds = typeof ts.seconds === 'bigint' ? Number(ts.seconds) : Number(ts.seconds);
            const nanos = Number(ts.nanos ?? 0);
            if (Number.isFinite(seconds) && Number.isFinite(nanos)) {
                const d = new Date(seconds * 1000 + Math.floor(nanos / 1000000));
                return Number.isNaN(d.getTime()) ? '' : d.toISOString();
            }
        }
    }
    return '';
}
function parseJsonObject(value) {
    if (typeof value !== 'string' || !value.trim()) return {};
    try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
        }
    } catch  {
    // Keep API resilient to malformed or legacy payloads.
    }
    return {};
}
function parseJsonArray(value) {
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch  {
        return [];
    }
}
function mapScenarioSummary(raw) {
    const scenario = toPlainObject(raw);
    return {
        id: String(readField(scenario, 'id', 'pricingId', 'scenarioId') ?? ''),
        projectId: String(readField(scenario, 'projectId', 'project_id') ?? ''),
        scenarioName: String(readField(scenario, 'scenarioName', 'scenario_name') ?? ''),
        isPrimary: Boolean(readField(scenario, 'isPrimary', 'is_primary')),
        createdAt: toIsoString(readField(scenario, 'createdAt', 'created_at')),
        updatedAt: toIsoString(readField(scenario, 'updatedAt', 'updated_at'))
    };
}
function mapScenarioDetail(raw) {
    const scenario = toPlainObject(raw);
    const summary = mapScenarioSummary(scenario);
    const parsedData = parseJsonObject(readField(scenario, 'data'));
    const rowIsPrimaryValue = readField(scenario, 'isPrimary', 'is_primary');
    const rowIsPrimary = typeof rowIsPrimaryValue === 'boolean' ? rowIsPrimaryValue : undefined;
    const parsedCreatedAt = typeof parsedData.createdAt === 'string' ? parsedData.createdAt : toIsoString(parsedData.createdAt);
    const parsedUpdatedAt = typeof parsedData.updatedAt === 'string' ? parsedData.updatedAt : toIsoString(parsedData.updatedAt);
    return {
        ...parsedData,
        id: summary.id || String(parsedData.id ?? ''),
        projectId: summary.projectId || String(parsedData.projectId ?? ''),
        scenarioName: summary.scenarioName || String(parsedData.scenarioName ?? ''),
        isPrimary: rowIsPrimary ?? Boolean(parsedData.isPrimary),
        companyId: String(parsedData.companyId ?? ''),
        createdAt: summary.createdAt || parsedCreatedAt || '',
        updatedAt: summary.updatedAt || parsedUpdatedAt || ''
    };
}
function mapIndirectRateConfig(raw) {
    const cfg = toPlainObject(raw);
    return {
        id: String(readField(cfg, 'id', 'configId') ?? ''),
        companyId: String(readField(cfg, 'companyId', 'company_id') ?? ''),
        name: String(readField(cfg, 'name') ?? ''),
        isDefault: Boolean(readField(cfg, 'isDefault', 'is_default')),
        pools: parseJsonArray(readField(cfg, 'pools')),
        gaBaseMethod: String(readField(cfg, 'gaBaseMethod', 'ga_base_method') ?? ''),
        useMultiplicative: Boolean(readField(cfg, 'useMultiplicative', 'use_multiplicative')),
        createdAt: toIsoString(readField(cfg, 'createdAt', 'created_at')),
        updatedAt: toIsoString(readField(cfg, 'updatedAt', 'updated_at'))
    };
}
// Rates live on the scenario's rate card (PRP-13): a rate is written only
// through SetPricingRate, and UpdateLCAT refuses a changed rate. The LCAT
// row's rate is therefore only a fallback for categories whose scenario has
// no base rate yet — it must never overwrite one the scenario holds, or a
// just-saved rate would revert on the next getPricing.
//
// Best effort: a missing/erroring LCAT lookup leaves the rate card as-is.
const BASE_RATE_KEYS = [
    'default',
    'base'
];
function hasBaseRate(category) {
    const rates = category.rates ?? {};
    return BASE_RATE_KEYS.some((key)=>{
        const param = rates[key];
        return !!param && typeof param === 'object' && typeof param.value === 'number' && param.value > 0;
    });
}
async function overlayCanonicalLCATRates(projectId, pricing) {
    if (!pricing.rateCard || pricing.rateCard.categories.every(hasBaseRate)) {
        return pricing;
    }
    let canonical;
    try {
        const lcatsResp = await proposalProjectApiConnect.getLCATs(projectId, {
            includePricing: false
        });
        canonical = lcatsResp.laborCategories;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[pricingApi.overlay] failed to load LCATs for overlay', {
            projectId,
            err
        });
        return pricing;
    }
    const ratesByLcatId = new Map();
    for (const lcat of canonical){
        if (typeof lcat.baseHourlyRate === 'number' && lcat.baseHourlyRate > 0) {
            ratesByLcatId.set(lcat.code, lcat.baseHourlyRate);
        }
    }
    return {
        ...pricing,
        rateCard: {
            ...pricing.rateCard,
            categories: pricing.rateCard.categories.map((category)=>{
                const canonicalRate = ratesByLcatId.get(category.laborCategoryId);
                if (canonicalRate === undefined || hasBaseRate(category)) return category;
                return {
                    ...category,
                    rates: {
                        ...category.rates,
                        default: {
                            name: 'rate',
                            label: 'Base Hourly Rate',
                            paramType: 'rate',
                            source: 'default',
                            metadata: {
                                displayOnlyRate: true
                            },
                            value: canonicalRate,
                            perPeriod: false
                        }
                    }
                };
            })
        }
    };
}
const RATE_CARD_EDIT_REASON = 'Rate card edit';
// SetPricingRate takes a positive dollar amount with at most two decimals.
function toCents(value) {
    return typeof value === 'number' && value > 0 ? Math.round(value * 100) : undefined;
}
// "default" is the base period (the server reads it as "base").
function periodKeyOf(rateKey) {
    return rateKey === 'default' ? 'base' : rateKey;
}
// Splits a rate-card save into the edited rates (sent through SetPricingRate,
// which records the reason and basis) and the card to store with UpdatePricing,
// where each edited rate is held at its stored value so SetPricingRate sees
// the true "from" value. Display-only fallbacks are omitted; explicit first
// rates are also written through SetPricingRate.
function splitRateEdits(stored, edited) {
    const storedById = new Map((stored?.categories ?? []).map((c)=>[
            c.id,
            c
        ]));
    const edits = [];
    const categories = edited.categories.map((category)=>{
        const before = storedById.get(category.id);
        const rates = {
            ...category.rates
        };
        for (const [key, param] of Object.entries(category.rates ?? {})){
            const cents = toCents(param && typeof param === 'object' ? param.value : undefined);
            const storedParam = before?.rates?.[key] ?? (key === 'default' ? before?.rates?.base : key === 'base' ? before?.rates?.default : undefined);
            if (param.metadata?.displayOnlyRate) {
                delete rates[key];
                continue;
            }
            if (cents === undefined) continue;
            if (cents === toCents(storedParam?.value)) continue;
            edits.push({
                categoryId: category.id,
                periodKey: periodKeyOf(key),
                cents
            });
            if (storedParam) rates[key] = storedParam;
            else delete rates[key];
        }
        return {
            ...category,
            rates
        };
    });
    return {
        edits,
        card: {
            ...edited,
            categories
        }
    };
}
export const pricingApiConnect = {
    async listPricing (projectId) {
        const response = await projectDataClient.listPricing({
            projectId
        });
        return {
            scenarios: (response.scenarios ?? []).map((scenario)=>mapScenarioSummary(scenario))
        };
    },
    async getPricing (projectId, pricingId) {
        try {
            const response = await projectDataClient.getPricing({
                projectId,
                pricingId
            });
            const pricing = mapScenarioDetail(response.scenario);
            return {
                pricing: await overlayCanonicalLCATRates(projectId, pricing)
            };
        } catch (error) {
            // Fallback: list includes full scenario payloads in this service contract.
            // If direct lookup fails due legacy ID drift, recover from list response.
            const listResponse = await projectDataClient.listPricing({
                projectId
            });
            const scenarios = listResponse.scenarios ?? [];
            const found = scenarios.find((s)=>mapScenarioSummary(s).id === pricingId);
            if (found) {
                const pricing = mapScenarioDetail(found);
                return {
                    pricing: await overlayCanonicalLCATRates(projectId, pricing)
                };
            }
            throw error;
        }
    },
    async createPricing (projectId, body) {
        const response = await projectDataClient.createPricing({
            projectId,
            scenarioName: body.scenarioName ?? '',
            seedFromExtractions: body.seedFromExtractions ?? false,
            indirectRateConfigId: body.indirectRateConfigId ?? ''
        });
        return {
            pricing: mapScenarioSummary(response.scenario)
        };
    },
    async updatePricing (projectId, pricingId, body) {
        let data = body;
        let edits = [];
        if (body.rateCard) {
            const current = await projectDataClient.getPricing({
                projectId,
                pricingId
            });
            const split = splitRateEdits(mapScenarioDetail(current.scenario).rateCard, body.rateCard);
            edits = split.edits;
            data = {
                ...body,
                rateCard: split.card
            };
        }
        const response = await projectDataClient.updatePricing({
            projectId,
            pricingId,
            data: JSON.stringify(data)
        });
        if (edits.length > 0) {
            // Each write moves the scenario's updated_at, so chain the token.
            let expectedUpdatedAt = (await projectDataClient.getPricingRateBook({
                projectId,
                scenarioId: pricingId
            })).scenarioUpdatedAt;
            for (const edit of edits){
                const written = await projectDataClient.setPricingRate({
                    projectId,
                    scenarioId: pricingId,
                    categoryId: edit.categoryId,
                    periodKey: edit.periodKey,
                    basis: 'manual',
                    amount: (edit.cents / 100).toFixed(2),
                    reason: RATE_CARD_EDIT_REASON,
                    expectedUpdatedAt
                });
                expectedUpdatedAt = written.scenarioUpdatedAt;
            }
        }
        return {
            pricing: mapScenarioSummary(response.scenario)
        };
    },
    async deletePricing (projectId, pricingId) {
        await projectDataClient.deletePricing({
            projectId,
            pricingId
        });
        return {
            success: true
        };
    },
    async listIndirectRates () {
        const response = await projectDataClient.listIndirectRates({});
        return {
            configs: (response.configs ?? []).map((config)=>mapIndirectRateConfig(config))
        };
    },
    async createIndirectRate (body) {
        const response = await projectDataClient.createIndirectRate({
            name: body.name ?? '',
            isDefault: body.isDefault ?? false,
            pools: JSON.stringify(body.pools ?? []),
            gaBaseMethod: body.gaBaseMethod ?? '',
            useMultiplicative: body.useMultiplicative ?? false
        });
        return {
            config: mapIndirectRateConfig(response.config)
        };
    },
    async updateIndirectRate (configId, body) {
        const response = await projectDataClient.updateIndirectRate({
            configId,
            name: body.name ?? '',
            isDefault: body.isDefault ?? false,
            pools: body.pools ? JSON.stringify(body.pools) : '',
            gaBaseMethod: body.gaBaseMethod ?? '',
            useMultiplicative: body.useMultiplicative ?? false
        });
        return {
            config: mapIndirectRateConfig(response.config)
        };
    },
    async deleteIndirectRate (configId) {
        await projectDataClient.deleteIndirectRate({
            configId
        });
        return {
            success: true
        };
    },
    listEvidence (projectId, pricingId) {
        return projectDataClient.listPricingEvidence({
            projectId,
            pricingId
        });
    },
    async snapshotVersion (projectId, pricingId) {
        return (await projectDataClient.snapshotPricingVersion({
            projectId,
            pricingId
        })).version;
    },
    resolveProposal (projectId, proposalId, accept) {
        return projectDataClient.resolvePricingProposal({
            projectId,
            proposalId,
            accept
        });
    }
};
